import { buildWordTool, type WordTool } from './builder';

export const readingTools: WordTool[] = [
  buildWordTool({
    name: 'get_document',
    description:
      'Read the document body: paragraphs (index, text, style), tables, properties, section count. Does NOT include headers, footers, or comments — use get_comments for those. Call this FIRST before any write, format, delete, or comment operation. Do NOT re-read after every change — only when indices or text have shifted. By default, font details are OMITTED to save tokens — pass include_font_details=true only when you need per-paragraph font info.',
    parameters: {
      type: 'object',
      properties: {
        start_index: {
          type: 'number',
          description:
            'Zero-based paragraph index to start from. Use with count to paginate when total_paragraphs exceeds 100. Omit to start at 0.',
        },
        count: {
          type: 'number',
          description:
            'Number of paragraphs to return (max 100). Omit for up to 100 from start_index.',
        },
        include_font_details: {
          type: 'boolean',
          description:
            'Include per-paragraph font name/size/color/bold/italic/underline/highlight. Defaults to false (omitted). Set true only when you specifically need font diagnostics — doubles response size.',
        },
      },
    },
    validateInput: (args) => {
      if (typeof args.start_index === 'number' && args.start_index < 0) {
        return { error: 'start_index must be >= 0.', recovery: 'Use 0 to start from the beginning.' };
      }
      if (typeof args.count === 'number' && args.count < 1) {
        return { error: 'count must be >= 1.', recovery: 'Use a value from 1 to 100.' };
      }
      if (typeof args.count === 'number' && args.count > 100) {
        return {
          error: `count ${args.count} exceeds maximum of 100.`,
          recovery: 'Use count <= 100 and paginate with start_index for large documents.',
        };
      }
      return null;
    },
    getActivityDescription: (args) => {
      const start = typeof args.start_index === 'number' ? args.start_index : 0;
      const count = typeof args.count === 'number' ? args.count : 100;
      return start === 0 && count === 100
        ? 'Reading document'
        : `Reading paragraphs ${start}–${start + count - 1}`;
    },
    execute: async (args) => {
      const includeFonts = args.include_font_details === true;
      return await Word.run(async (context) => {
        const doc = context.document;
        const body = doc.body;
        const paragraphs = body.paragraphs;
        const props = doc.properties;
        const sections = doc.sections;

        body.load('text');
        paragraphs.load('items');
        props.load('title,author,subject,keywords,lastAuthor,revisionNumber');
        sections.load('items');
        await context.sync();

        const start = typeof args.start_index === 'number' ? args.start_index : 0;
        const count = typeof args.count === 'number' ? args.count : 100;
        const slice = paragraphs.items.slice(start, start + count);
        const fonts: Word.Font[] = [];
        for (const p of slice) {
          p.load('text,styleBuiltIn');
          if (includeFonts) {
            const font = p.getRange().font;
            font.load('name,size,color,bold,italic,underline,highlightColor');
            fonts.push(font);
          }
        }
        await context.sync();

        const tables = body.tables;
        tables.load('items');
        await context.sync();

        for (const table of tables.items) {
          table.load('rowCount,values');
        }
        await context.sync();

        const tableInfo = tables.items.map((t: any, i: number) => ({
          index: i,
          rows: t.rowCount,
          columns: (t.values[0] as string[] | undefined)?.length ?? 0,
          first_row: (t.values[0] as string[] | undefined) ?? [],
        }));

        const response: Record<string, unknown> = {
          text: body.text,
          paragraphs: slice.map((p, i) => {
            const base: Record<string, unknown> = {
              index: start + i,
              text: p.text,
              style: p.styleBuiltIn,
            };
            if (includeFonts) {
              base.font = {
                name: fonts[i]!.name,
                size: fonts[i]!.size,
                color: fonts[i]!.color,
                bold: fonts[i]!.bold,
                italic: fonts[i]!.italic,
                underline: fonts[i]!.underline,
                highlight: fonts[i]!.highlightColor,
              };
            }
            return base;
          }),
          total_paragraphs: paragraphs.items.length,
          tables: tableInfo,
          properties: {
            title: props.title,
            author: props.author,
            subject: props.subject,
            keywords: props.keywords,
            lastAuthor: props.lastAuthor,
            revisionNumber: props.revisionNumber,
          },
          sections: sections.items.length,
        };

        if (
          paragraphs.items.length === 0 ||
          (paragraphs.items.length === 1 && !paragraphs.items[0]!.text.trim())
        ) {
          response.hint = 'Document is empty. Use insert_content to add content.';
        }

        return JSON.stringify(response);
      });
    },
  }),

  buildWordTool({
    name: 'get_selection',
    description:
      'Get the currently selected (highlighted) text in the document. Returns the selected text, or an empty string if nothing is selected. Call this BEFORE insert_content with location="replace_selection" or "after_selection" to verify the selection exists. NOT required before format_text — format_text has built-in text targeting via query/paragraph_index. If the result is empty and you need a selection, use select_text to programmatically select text.',
    parameters: { type: 'object', properties: {} },
    getActivityDescription: () => 'Reading selection',
    execute: async () => {
      return await Word.run(async (context) => {
        const selection = context.document.getSelection();
        selection.load('text');
        await context.sync();

        const response: Record<string, unknown> = { text: selection.text };
        if (!selection.text) {
          response.hint =
            'No text is selected. Use select_text to select text programmatically, or ask the user to highlight text.';
        }
        return JSON.stringify(response);
      });
    },
  }),

  buildWordTool({
    name: 'select_text',
    description:
      "NICHE. Only call this when you specifically need to show the user a VISIBLE highlighted range (e.g., 'let me show you where X is'). For any actual operation, the direct-targeting tools are better and faster: insert_content accepts target_query to find-and-replace; format_text accepts query or queries[] for formatting; search_document for find/replace. If you find yourself calling select_text before another tool, stop — use the direct-targeting parameter instead.",
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description:
            'Exact text to search for and select. Use 3+ unique words to avoid multiple matches. The search is case-insensitive. If the text is not found, the error will suggest using get_document to verify the exact text.',
        },
        match_index: {
          type: 'number',
          description:
            'Which match to select when query finds multiple results (0-based). Check total_matches in the response to see how many exist. Omit to select the first match (index 0).',
        },
        paragraph_index: {
          type: 'number',
          description:
            'Select an entire paragraph by its zero-based index. Get valid indices from get_document first. Cannot be combined with query.',
        },
      },
    },
    validateInput: (args) => {
      if (typeof args.query !== 'string' && typeof args.paragraph_index !== 'number') {
        return {
          error: 'Provide either query or paragraph_index.',
          recovery:
            'Use query to select specific text, or paragraph_index to select a whole paragraph. Call get_document to see available text and indices.',
        };
      }
      if (typeof args.query === 'string' && typeof args.paragraph_index === 'number') {
        return {
          error: 'Provide either query or paragraph_index, not both.',
          recovery: 'Drop whichever is less precise for your target.',
        };
      }
      return null;
    },
    getActivityDescription: (args) => {
      if (typeof args.paragraph_index === 'number') return `Selecting paragraph ${args.paragraph_index}`;
      if (typeof args.query === 'string') {
        const preview = args.query.length > 40 ? args.query.slice(0, 40) + '…' : args.query;
        return `Selecting "${preview}"`;
      }
      return 'Selecting text';
    },
    execute: async (args) => {
      return await Word.run(async (context) => {
        if (typeof args.query === 'string') {
          const results = context.document.body.search(args.query as string, {
            matchCase: false,
          });
          results.load('items');
          await context.sync();

          if (results.items.length === 0) {
            return JSON.stringify({
              error: `Text "${args.query}" not found in the document.`,
              recovery:
                'Use get_document to check the exact text. Even small differences in spacing or punctuation cause mismatches.',
            });
          }

          const idx = typeof args.match_index === 'number' ? args.match_index : 0;
          if (idx < 0 || idx >= results.items.length) {
            return JSON.stringify({
              error: `match_index ${idx} out of range (${results.items.length} matches found).`,
              recovery: `Use a value from 0 to ${results.items.length - 1}.`,
            });
          }

          if (results.items.length > 1) {
            for (const item of results.items) {
              item.load('text');
            }
            await context.sync();
          }

          results.items[idx]!.select();
          await context.sync();

          const response: Record<string, unknown> = {
            success: true,
            selected: args.query,
            match_index: idx,
            total_matches: results.items.length,
          };

          if (results.items.length > 1) {
            response.hint = `${results.items.length} matches found. Selected match ${idx}. Use a longer query or match_index to target a specific one.`;
          }

          return JSON.stringify(response);
        } else {
          const paragraphs = context.document.body.paragraphs;
          paragraphs.load('items');
          await context.sync();

          const idx = args.paragraph_index as number;
          const p = paragraphs.items[idx];
          if (!p) {
            return JSON.stringify({
              error: `Paragraph index ${idx} out of range (document has ${paragraphs.items.length} paragraphs).`,
              recovery: `Use an index from 0 to ${paragraphs.items.length - 1}. Call get_document to see valid indices.`,
            });
          }

          p.select();
          await context.sync();
          p.load('text');
          await context.sync();
          return JSON.stringify({ success: true, selected_paragraph: idx, text: p.text });
        }
      });
    },
  }),
];
